/**
 * Recipient row rules, ported from the prototype's `problems(row)` and `CHANNELS[*].validate`.
 *
 * One function decides a row's issues for both sides: the mock backend uses it to answer like
 * api-service, and the recipients table uses it for instant feedback while a cell is edited.
 * Server-only checks (Slack app membership) arrive through `slackChannelsWithoutApp`; the UI
 * leaves it out and keeps the server's verdict with `mergeRecipientIssues`.
 */
import { DISTRIBUTION_CHANNELS, EMAIL_PATTERN, isLiveChannel } from "./distributionChannels";
import { EXAMPLE_ACCENT, EXAMPLE_CC, EXAMPLE_EMAIL, EXAMPLE_LOGO_URL } from "./distributionExamples";
import { andList, orList } from "./distributionFormat";
import {
  LIMITS,
  type ChannelApi,
  type CoverageApi,
  type CoverageValueApi,
  type RecipientApi,
  type RecipientInputApi,
  type RecipientIssueApi,
  type RecipientIssueCodeApi,
  type RecipientSummaryApi,
  type UncoveredPolicyApi,
} from "./distributions.types";

export interface RecipientRuleContext {
  /** Split field label, e.g. "Region". */
  fieldLabel: string;
  /** Plural label, e.g. "Regions"; used when the value list is long. */
  fieldPlural?: string;
  /** Every value of the split field in the data. Empty = unknown, so value checks are skipped. */
  values: readonly string[];
  excludedValues: readonly string[];
  templateIds: readonly string[];
  /** null = theme list unknown here, so the theme check is skipped. */
  themeIds: readonly string[] | null;
  /** All rows, in order, for the duplicate check. */
  rows: readonly RecipientInputApi[];
  /** Server-only: Slack channels the MagicBI app isn't in. */
  slackChannelsWithoutApp?: ReadonlySet<string>;
  /**
   * Server-only, and only with an `inside` audience: the tenant's own email domains (its active
   * members' domains, lower-cased). An address or cc outside them is an `outside_address` error.
   */
  insideDomains?: ReadonlySet<string>;
}

/** Codes only api-service can decide; the UI keeps them from the last server read. */
export const SERVER_ONLY_ISSUE_CODES: readonly RecipientIssueCodeApi[] = [
  "slack_not_connected",
  "slack_channel_not_found",
  "slack_app_not_in_channel",
  "outside_address",
];

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;
const LOGO_PATTERN = /^https:\/\/\S+\.(png|jpe?g)(\?\S*)?$/i;

/** A logo link copies can draw (magic-core `LOGO_URL_PATTERN`): an https link to a PNG or JPG. */
export const isLogoUrl = (url: string): boolean => LOGO_PATTERN.test(url.trim());

/** What a logo link must be, with an example; recipient rows and the default branding say the same. */
export const LOGO_URL_RULE = `The logo needs an https link to a PNG or JPG image, such as ${EXAMPLE_LOGO_URL}.`;

const issue = (
  code: RecipientIssueCodeApi,
  column: RecipientIssueApi["column"],
  message: string,
  severity: RecipientIssueApi["severity"] = "error",
): RecipientIssueApi => ({ code, severity, column, message });

/** The value as the data spells it, matched trimmed and case-insensitively; null when absent. */
export const canonicalValue = (raw: string, values: readonly string[]): string | null => {
  const key = raw.trim().toLowerCase();
  return values.find((value) => value.toLowerCase() === key) ?? null;
};

const valueChoices = (ctx: RecipientRuleContext): string => {
  if (ctx.values.length > 6) {
    return `one of the ${ctx.values.length} ${ctx.fieldPlural ?? `${ctx.fieldLabel} values`} in the story`;
  }
  return orList(ctx.values);
};

const valueIssues = (row: RecipientInputApi, ctx: RecipientRuleContext): RecipientIssueApi[] => {
  const raw = row.value.trim();
  if (!raw) {
    return [issue("missing_value", "value", `Add the ${ctx.fieldLabel} this person should get. Choose ${valueChoices(ctx)}.`)];
  }
  if (!ctx.values.length) return [];
  const canonical = canonicalValue(raw, ctx.values);
  if (!canonical) {
    return [
      issue("value_not_in_data", "value", `“${raw}” isn’t a ${ctx.fieldLabel} in this story. Choose ${valueChoices(ctx)}.`),
    ];
  }
  const out: RecipientIssueApi[] = [];
  if (canonical !== row.value) {
    out.push(issue("value_normalized", "value", `“${row.value}” is written differently in the story, so we read it as ${canonical}.`, "warning"));
  }
  if (ctx.excludedValues.includes(canonical)) {
    out.push(
      issue("value_excluded", "value", `${canonical} is left out in Split by, so this person gets nothing. Include ${canonical} in step 1, or remove this row.`, "warning"),
    );
  }
  return out;
};

const ISSUE_CODE_BY_CHANNEL: Partial<Record<ChannelApi, RecipientIssueCodeApi>> = {
  email: "invalid_email",
  teams: "invalid_teams_webhook",
};

const addressIssues = (row: RecipientInputApi, ctx: RecipientRuleContext): RecipientIssueApi[] => {
  const channel = DISTRIBUTION_CHANNELS[row.channel] ?? DISTRIBUTION_CHANNELS.email;
  if (!isLiveChannel(channel.id)) {
    return [
      issue("channel_coming_soon", "channel", `${channel.label} is coming soon. Send this person’s copy by email or Slack for now.`),
    ];
  }
  const address = row.address.trim();
  if (!address) {
    return [issue("missing_address", "address", channel.validate(""))];
  }
  const message = channel.validate(address);
  if (message) {
    return [issue(ISSUE_CODE_BY_CHANNEL[channel.id] ?? "invalid_slack_channel", "address", message)];
  }
  if (channel.id === "slack" && ctx.slackChannelsWithoutApp?.has(address.toLowerCase())) {
    return [
      issue("slack_app_not_in_channel", "address", `MagicBI isn’t in ${address} yet. In Slack, open the channel, type /invite @MagicBI, then click Recheck.`),
    ];
  }
  return [];
};

const ccIssues = (row: RecipientInputApi): RecipientIssueApi[] => {
  const cc = row.cc.map((entry) => entry.trim()).filter(Boolean);
  if (!cc.length) return [];
  if (row.channel !== "email") {
    return [issue("cc_ignored", "cc", `Cc only works for email, so it’s left off this ${DISTRIBUTION_CHANNELS[row.channel]?.label ?? "post"}.`, "warning")];
  }
  if (cc.some((entry) => entry.includes(","))) {
    return [issue("invalid_cc", "cc", `Separate cc addresses with semicolons, not commas, such as ${EXAMPLE_CC}.`)];
  }
  const bad = cc.find((entry) => !EMAIL_PATTERN.test(entry));
  if (bad) {
    return [issue("invalid_cc", "cc", `“${bad}” in cc isn’t a full email address. Use addresses such as ${EXAMPLE_EMAIL}, separated by semicolons.`)];
  }
  if (cc.length > LIMITS.maxCc) {
    return [issue("invalid_cc", "cc", `Add up to ${LIMITS.maxCc} cc addresses. This row has ${cc.length}.`)];
  }
  return [];
};

const brandIssues = (row: RecipientInputApi): RecipientIssueApi[] => {
  const out: RecipientIssueApi[] = [];
  const brand = row.brand;
  if (brand?.logo_url && !isLogoUrl(brand.logo_url)) {
    out.push(issue("invalid_logo_url", "brand", `${LOGO_URL_RULE} The default logo is used until then.`, "warning"));
  }
  if (brand?.accent && !HEX_PATTERN.test(brand.accent.trim())) {
    out.push(issue("invalid_accent", "brand", `“${brand.accent}” isn’t a colour. Use a hex colour such as ${EXAMPLE_ACCENT}. The default colour is used until then.`, "warning"));
  }
  return out;
};

/** "name@company.com" → "company.com"; "" when it isn't an address. */
export const emailDomainOf = (address: string): string => /@([^@\s]+)$/.exec(address.trim().toLowerCase())?.[1] ?? "";

/** The server's wording (§4.1): the address is outside, why that matters, and the two ways out. */
export const outsideAddressMessage = (addresses: readonly string[], column: "address" | "cc"): string => {
  const one = addresses.length === 1;
  const who = andList(addresses);
  return column === "address"
    ? `${who} is outside your organisation, so copies for people inside can’t go to it. Choose people outside your organisation in step 1, or remove this row.`
    : `${who} in cc ${one ? "is" : "are"} outside your organisation, so copies for people inside can’t go to ${one ? "it" : "them"}. Choose people outside your organisation in step 1, or remove ${one ? "it" : "them"} from cc.`;
};

/** With an inside audience, email addresses and cc outside the tenant's domains (server-only). */
const outsideIssues = (row: RecipientInputApi, ctx: RecipientRuleContext): RecipientIssueApi[] => {
  const domains = ctx.insideDomains;
  if (!domains || row.channel !== "email") return [];
  const outside = (entry: string) => EMAIL_PATTERN.test(entry) && !domains.has(emailDomainOf(entry));
  const out: RecipientIssueApi[] = [];
  const address = row.address.trim();
  if (outside(address)) out.push(issue("outside_address", "address", outsideAddressMessage([address], "address")));
  const cc = row.cc.map((entry) => entry.trim()).filter(outside);
  if (cc.length) out.push(issue("outside_address", "cc", outsideAddressMessage(cc, "cc")));
  return out;
};

const sameText = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Same value, channel and address as an earlier row (magic-core `duplicate_issues`); rows without an address are left to `missing_address`. */
const duplicateIssue = (row: RecipientInputApi, index: number, ctx: RecipientRuleContext): RecipientIssueApi[] => {
  if (!row.address.trim()) return [];
  const first = ctx.rows.findIndex(
    (other, j) =>
      j < index &&
      other.channel === row.channel &&
      sameText(other.value, row.value) &&
      sameText(other.address, row.address),
  );
  return first < 0
    ? []
    : [issue("duplicate", null, `Same ${ctx.fieldLabel}, channel and address as row ${first + 1}. Remove one of them.`)];
};

/** Every issue for the row at `index` of `ctx.rows`, errors first in the prototype's order. */
export const getRecipientIssues = (
  row: RecipientInputApi,
  index: number,
  ctx: RecipientRuleContext,
): RecipientIssueApi[] => {
  const out: RecipientIssueApi[] = [];
  if (!row.name.trim()) out.push(issue("missing_name", "name", "Add a name so you can tell recipients apart."));
  out.push(...valueIssues(row, ctx), ...addressIssues(row, ctx), ...ccIssues(row), ...outsideIssues(row, ctx));
  if (row.template_id && !ctx.templateIds.includes(row.template_id)) {
    out.push(
      issue("unknown_template", "template_id", `There’s no template called “${row.template_id}”. Choose one from the list, or pick Default to use the one from step 3.`),
    );
  }
  if (row.theme_id && ctx.themeIds && !ctx.themeIds.includes(row.theme_id)) {
    out.push(issue("unknown_theme", "theme_id", `There’s no theme called “${row.theme_id}”. The default theme is used until you choose one from the list.`, "warning"));
  }
  out.push(...brandIssues(row), ...duplicateIssue(row, index, ctx));
  return out;
};

export const hasBlockingIssue = (issues: readonly RecipientIssueApi[]): boolean =>
  issues.some((entry) => entry.severity === "error");

/**
 * Client issues for an edited row, plus the server-only ones from its last read (the client
 * can't know whether the Slack app is in a channel).
 */
export const mergeRecipientIssues = (
  serverIssues: readonly RecipientIssueApi[],
  clientIssues: readonly RecipientIssueApi[],
): RecipientIssueApi[] => [
  ...clientIssues,
  ...serverIssues.filter((entry) => SERVER_ONLY_ISSUE_CODES.includes(entry.code)),
];

/** A stored row as GET /recipients returns it: canonical value, issues and status. */
export const toRecipientApi = (
  row: RecipientInputApi,
  index: number,
  ctx: RecipientRuleContext,
  id: string,
): RecipientApi => {
  const issues = getRecipientIssues(row, index, ctx);
  const canonical = canonicalValue(row.value, ctx.values);
  return {
    ...row,
    value: canonical ?? row.value,
    id,
    status: hasBlockingIssue(issues) ? "fix" : "ready",
    issues,
    slack_channel_name: row.channel === "slack" && row.address.trim().startsWith("#") ? row.address.trim() : null,
  };
};

export const summarizeRecipients = (rows: readonly RecipientApi[]): RecipientSummaryApi => {
  const byChannel: Partial<Record<ChannelApi, number>> = {};
  rows.forEach((row) => {
    byChannel[row.channel] = (byChannel[row.channel] ?? 0) + 1;
  });
  const toFix = rows.filter((row) => row.status === "fix").length;
  return { total: rows.length, ready: rows.length - toFix, to_fix: toFix, by_channel: byChannel };
};

export interface CoverageInput {
  /** Every value in the data; empty = unknown. */
  values: readonly string[];
  excludedValues: readonly string[];
  /** Values with no data, left out when `skipEmptyValues` is on. */
  emptyValues: readonly string[];
  skipEmptyValues: boolean;
  rows: readonly RecipientApi[];
}

/** Included values of the split field, in data order. */
export const includedValues = (input: Omit<CoverageInput, "rows">): string[] =>
  input.values.filter(
    (value) =>
      !input.excludedValues.includes(value) && !(input.skipEmptyValues && input.emptyValues.includes(value)),
  );

/** Which included values have someone to receive them (prototype `coverage()`). */
export const computeCoverage = (input: CoverageInput): CoverageApi => {
  if (!input.values.length) {
    return { state: "unknown", total_values: 0, covered_values: 0, by_value: [], uncovered: [], unknown_values: [] };
  }
  const included = includedValues(input);
  const byValue: CoverageValueApi[] = included.map((value) => {
    const people = input.rows.filter((row) => row.value === value);
    const toFix = people.filter((row) => row.status === "fix").length;
    return {
      value,
      recipients: people.length,
      to_fix: toFix,
      state: !people.length ? "none" : toFix ? "fix" : "ok",
    };
  });
  input.excludedValues
    .filter((value) => input.rows.some((row) => row.value === value))
    .forEach((value) => {
      const people = input.rows.filter((row) => row.value === value);
      byValue.push({ value, recipients: people.length, to_fix: people.filter((r) => r.status === "fix").length, state: "excluded" });
    });
  const unknown = [
    ...Array.from(new Set(input.rows.map((row) => row.value).filter((value) => value.trim() && !input.values.includes(value)))),
  ];
  return {
    state: "known",
    total_values: included.length,
    covered_values: byValue.filter((entry) => entry.state !== "excluded" && entry.recipients > 0).length,
    by_value: byValue,
    uncovered: byValue.filter((entry) => entry.state === "none").map((entry) => entry.value),
    unknown_values: unknown,
  };
};

/** "Hold the copy…" / "Skip it…" sentence for the uncovered-values check. */
export const uncoveredSentence = (uncovered: readonly string[], policy: UncoveredPolicyApi): string => {
  const one = uncovered.length === 1;
  const list = andList(uncovered);
  const lead = `No one gets the ${list} ${one ? "copy" : "copies"}.`;
  return policy === "hold"
    ? `${lead} ${one ? "It’s" : "They’re"} held, and you’ll get a reminder before each send.`
    : `${lead} ${one ? "It’s" : "They’re"} skipped until you add someone.`;
};
